import React, {useState, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {displayService, photoService} from '../services';
import {Display, DisplayPhoto, Photo} from '../types';
import {useSettingsNavigation} from '../hooks/useSettingsNavigation';
import {useRouting} from '../hooks/useRouting';
import {useAppDispatch} from '../store';
import {logout} from '../store/authSlice';
import {
  SettingsLayout,
  GlassPanel,
  Heading,
  SubHeading,
  BodyText,
  theme,
} from '../components/DarkTheme';
import OfflineBanner from '../components/OfflineBanner';
import {useDataLoader} from '../hooks/useDataLoader';

export default function DisplayPhotosScreen() {
  const {t} = useTranslation();
  const {navigateToSettingsScreen, navigate} = useSettingsNavigation('DisplayPhotos');
  const dispatch = useAppDispatch();
  const {getRouteParam} = useRouting();
  const displayId = getRouteParam('displayId') || '';

  const [display, setDisplay] = useState<Display | null>(null);
  const [displayPhotos, setDisplayPhotos] = useState<DisplayPhoto[]>([]);
  const [allPhotos, setAllPhotos] = useState<Photo[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Get assigned photo IDs for quick lookup
  const assignedPhotoIds = new Set(displayPhotos.map(dp => dp.photoId));

  // Get available photos (not yet assigned to this display)
  const availablePhotos = allPhotos.filter(p => !assignedPhotoIds.has(p.id));

  const loadPhotoUrl = useCallback(async (photoId: string) => {
    try {
      const url = await photoService.getThumbnailUrl(photoId);
      setPhotoUrls(prev => ({...prev, [photoId]: url}));
    } catch (error) {
      console.error(`Failed to load URL for photo ${photoId}`, error);
    }
  }, []);

  const loadData = useCallback(async () => {
    const [displayData, photosResponse, allPhotosResponse] = await Promise.all([
      displayService.getDisplay(displayId),
      displayService.getDisplayPhotos(displayId),
      photoService.getSelectedPhotos(),
    ]);

    setDisplay(displayData);
    setDisplayPhotos(photosResponse.photos);
    setAllPhotos(allPhotosResponse.photos);

    // Load URLs for assigned photos
    photosResponse.photos.forEach(dp => {
      if (dp.hasS3Cache) {
        loadPhotoUrl(dp.photoId);
      }
    });

    // Load URLs for all photos
    allPhotosResponse.photos.forEach(p => {
      if (p.hasS3Cache) {
        loadPhotoUrl(p.id);
      }
    });
  }, [displayId, loadPhotoUrl]);

  const {isLoading, loadError, refreshControlProps} = useDataLoader(loadData);

  const handleAddPhoto = async (photoId: string) => {
    if (isAdding) return;
    setIsAdding(true);
    try {
      const newDisplayPhoto = await displayService.addPhotoToDisplay(displayId, {
        photoId,
      });
      setDisplayPhotos(prev => [...prev, newDisplayPhoto]);
      setDisplay(prev =>
        prev ? {...prev, photoCount: prev.photoCount + 1} : prev,
      );
    } catch (error) {
      console.error('Failed to add photo', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemovePhoto = async (photoId: string) => {
    try {
      await displayService.removePhotoFromDisplay(displayId, photoId);
      setDisplayPhotos(prev => prev.filter(dp => dp.photoId !== photoId));
      setDisplay(prev =>
        prev ? {...prev, photoCount: Math.max(0, prev.photoCount - 1)} : prev,
      );
    } catch (error) {
      console.error('Failed to remove photo', error);
    }
  };

  const handleLogout = async () => {
    await dispatch(logout());
  };

  if (isLoading && loadError !== 'network') {
    return (
      <SettingsLayout
        currentScreen="displays"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </SettingsLayout>
    );
  }

  if (!display) {
    return (
      <SettingsLayout
        currentScreen="displays"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          {loadError === 'network' ? (
            <>
              <OfflineBanner />
              <BodyText style={{color: theme.textMuted, textAlign: 'center', marginTop: 8}}>{t('common.offlineCannotLoad')}</BodyText>
            </>
          ) : (
            <BodyText>Display not found</BodyText>
          )}
        </View>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout
      currentScreen="displays"
      onNavigate={navigateToSettingsScreen}
      onLogout={handleLogout}>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl {...refreshControlProps} />
        }>
        <OfflineBanner />
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigate('DisplayEdit', {displayId})}
            activeOpacity={0.7}>
            <Text style={styles.backIcon}>arrow_back</Text>
            <Text style={styles.backText}>{display.name}</Text>
          </TouchableOpacity>
          <Heading>{t('displays.photos')}</Heading>
          <BodyText style={styles.subtitle}>
            {t('displays.photosDescription')}
          </BodyText>
        </View>

        {/* Assigned Photos */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <SubHeading>{t('displays.assignedPhotos')}</SubHeading>
            <Text style={styles.countBadge}>{displayPhotos.length}</Text>
          </View>

          {displayPhotos.length > 0 ? (
            <View style={styles.photosGrid}>
              {displayPhotos.map(dp => (
                <View key={dp.id} style={styles.photoItem}>
                  <View style={styles.photoImageContainer}>
                    {photoUrls[dp.photoId] ? (
                      <Image
                        source={{uri: photoUrls[dp.photoId]}}
                        style={styles.photoImage}
                      />
                    ) : (
                      <View style={styles.photoPlaceholder}>
                        <ActivityIndicator size="small" color={theme.textMuted} />
                      </View>
                    )}
                    <TouchableOpacity
                      style={styles.removeButton}
                      onPress={() => handleRemovePhoto(dp.photoId)}
                      activeOpacity={0.7}>
                      <Text style={styles.removeButtonText}>×</Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={styles.photoName} numberOfLines={1}>
                    {dp.filename || 'Photo'}
                  </Text>
                </View>
              ))}
            </View>
          ) : (
            <GlassPanel style={styles.emptyState}>
              <Text style={styles.emptyIcon}>photo_library</Text>
              <BodyText style={styles.emptyText}>
                {t('displays.noPhotosAssigned')}
              </BodyText>
              <BodyText style={styles.emptyHint}>
                {t('displays.addPhotosHint')}
              </BodyText>
            </GlassPanel>
          )}
        </View>

        {/* Add Photos Button */}
        {availablePhotos.length > 0 && (
          <TouchableOpacity
            style={styles.addButton}
            onPress={() => setShowAddModal(true)}
            activeOpacity={0.7}>
            <Text style={styles.addButtonIcon}>add</Text>
            <Text style={styles.addButtonText}>{t('displays.addPhoto')}</Text>
          </TouchableOpacity>
        )}

        {/* No Available Photos Message */}
        {availablePhotos.length === 0 && allPhotos.length > 0 && (
          <GlassPanel style={styles.infoPanel}>
            <Text style={styles.infoIcon}>info</Text>
            <BodyText style={styles.infoText}>
              All your photos are already assigned to this display.
            </BodyText>
          </GlassPanel>
        )}

        {/* No Photos at All */}
        {allPhotos.length === 0 && (
          <GlassPanel style={styles.infoPanel}>
            <Text style={styles.infoIcon}>photo_library</Text>
            <BodyText style={styles.infoText}>
              You haven't added any photos yet. Go to Gallery Settings to add
              photos from Google Photos.
            </BodyText>
            <TouchableOpacity
              style={styles.linkButton}
              onPress={() => navigate('GallerySettings')}
              activeOpacity={0.7}>
              <Text style={styles.linkButtonText}>Go to Gallery Settings</Text>
            </TouchableOpacity>
          </GlassPanel>
        )}
      </ScrollView>

      {/* Add Photo Modal */}
      <Modal
        visible={showAddModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAddModal(false)}>
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowAddModal(false)}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {t('displays.availablePhotos')}
              </Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowAddModal(false)}
                activeOpacity={0.7}>
                <Text style={styles.modalCloseIcon}>close</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalScrollView}>
              <View style={styles.modalPhotosGrid}>
                {availablePhotos.map(photo => (
                  <TouchableOpacity
                    key={photo.id}
                    style={styles.modalPhotoItem}
                    onPress={() => {
                      handleAddPhoto(photo.id);
                      if (availablePhotos.length === 1) {
                        setShowAddModal(false);
                      }
                    }}
                    disabled={isAdding}
                    activeOpacity={0.7}>
                    <View style={styles.modalPhotoImageContainer}>
                      {photoUrls[photo.id] ? (
                        <Image
                          source={{uri: photoUrls[photo.id]}}
                          style={styles.modalPhotoImage}
                        />
                      ) : (
                        <View style={styles.photoPlaceholder}>
                          <ActivityIndicator
                            size="small"
                            color={theme.textMuted}
                          />
                        </View>
                      )}
                      <View style={styles.addOverlay}>
                        <Text style={styles.addOverlayIcon}>add</Text>
                      </View>
                    </View>
                    <Text style={styles.modalPhotoName} numberOfLines={1}>
                      {photo.filename || 'Photo'}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>
        </Pressable>
      </Modal>
    </SettingsLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 24,
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Header
  header: {
    marginBottom: 32,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  backIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.primary,
  },
  backText: {
    color: theme.primary,
    fontSize: 16,
  },
  subtitle: {
    color: theme.textMuted,
    marginTop: 8,
  },

  // Section
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  countBadge: {
    backgroundColor: theme.primaryLight,
    color: theme.primary,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 14,
    fontWeight: '600',
  },

  // Photos Grid
  photosGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  photoItem: {
    width: 100,
    alignItems: 'center',
  },
  photoImageContainer: {
    position: 'relative',
    width: 100,
    height: 100,
  },
  photoImage: {
    width: 100,
    height: 100,
    borderRadius: 8,
    backgroundColor: theme.surface,
  },
  photoPlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 8,
    backgroundColor: theme.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.error,
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  photoName: {
    color: theme.textMuted,
    fontSize: 12,
    marginTop: 4,
    textAlign: 'center',
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    padding: 32,
  },
  emptyIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 48,
    color: theme.textMuted,
    marginBottom: 16,
  },
  emptyText: {
    color: theme.text,
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyHint: {
    color: theme.textMuted,
    textAlign: 'center',
    fontSize: 14,
  },

  // Add Button
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: theme.primary,
    paddingVertical: 14,
    borderRadius: 12,
    marginBottom: 24,
  },
  addButtonIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: '#fff',
  },
  addButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },

  // Info Panel
  infoPanel: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    flexWrap: 'wrap',
  },
  infoIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.textMuted,
  },
  infoText: {
    flex: 1,
    color: theme.textMuted,
    fontSize: 14,
    minWidth: 200,
  },
  linkButton: {
    width: '100%',
    marginTop: 12,
    paddingVertical: 10,
    backgroundColor: theme.primaryLight,
    borderRadius: 8,
    alignItems: 'center',
  },
  linkButtonText: {
    color: theme.primary,
    fontSize: 14,
    fontWeight: '500',
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.surface,
    borderRadius: 16,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  modalTitle: {
    color: theme.text,
    fontSize: 18,
    fontWeight: '600',
  },
  modalCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCloseIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.textMuted,
  },
  modalScrollView: {
    maxHeight: 400,
  },
  modalPhotosGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 16,
    gap: 12,
  },
  modalPhotoItem: {
    width: 80,
    alignItems: 'center',
  },
  modalPhotoImageContainer: {
    position: 'relative',
    width: 80,
    height: 80,
  },
  modalPhotoImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: theme.background,
  },
  addOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0,
  },
  addOverlayIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 32,
    color: '#fff',
  },
  modalPhotoName: {
    color: theme.textMuted,
    fontSize: 11,
    marginTop: 4,
    textAlign: 'center',
  },
});
